/**
 * Default Market Behavior
 *
 * Default configuration used when no market-specific override exists.
 * Based on US market as the primary market.
 */

import type {
  AddressFormat,
  DeliveryConfig,
  LegalConfig,
  MarketBehavior,
  TaxConfig,
} from './types';

/**
 * Default tax configuration (US-style sales tax)
 */
export const defaultTaxConfig: TaxConfig = {
  pricesIncludeTax: false, // US: Prices exclude tax
  showTaxSeparately: true,
  taxLabel: 'Sales Tax',
  specialTaxHandling: 'none',
};

/**
 * Default address format (US-style)
 */
export const defaultAddressFormat: AddressFormat = {
  fieldOrder: [
    'firstName',
    'lastName',
    'streetAddress1',
    'streetAddress2',
    'city',
    'countryArea',
    'postalCode',
    'country',
  ],
  requiredFields: [
    'firstName',
    'lastName',
    'streetAddress1',
    'city',
    'countryArea',
    'postalCode',
    'country',
  ],
  postalCodeRegex: '^\\d{5}(-\\d{4})?$', // US ZIP code
  stateLabel: 'state',
  showState: true,
};

/**
 * Default delivery configuration
 */
export const defaultDeliveryConfig: DeliveryConfig = {
  showDeliveryDates: true,
  allowScheduling: false,
  freeShippingThreshold: null,
};

/**
 * Default legal configuration
 */
export const defaultLegalConfig: LegalConfig = {
  withdrawalPeriod: 30,
  ageVerification: false,
  showCarbonFootprint: false,
};

/**
 * Complete default market behavior
 */
export const defaultMarketBehavior: MarketBehavior = {
  countryCode: 'US',
  channelSlug: 'us-usd',
  currency: 'USD',
  defaultLanguage: 'en',
  supportedLanguages: ['en'],
  tax: defaultTaxConfig,
  addressFormat: defaultAddressFormat,
  delivery: defaultDeliveryConfig,
  legal: defaultLegalConfig,
};

/**
 * Market-specific defaults by country
 * These provide sensible defaults for common markets
 */
const MARKET_DEFAULTS: Partial<Record<string, Partial<MarketBehavior>>> = {
  // United States
  US: {
    channelSlug: 'us-usd',
    currency: 'USD',
    defaultLanguage: 'en',
    tax: { ...defaultTaxConfig, taxLabel: 'Sales Tax' },
  },

  // United Kingdom
  GB: {
    channelSlug: 'gb-gbp',
    currency: 'GBP',
    defaultLanguage: 'en',
    tax: {
      pricesIncludeTax: true,
      showTaxSeparately: true,
      taxLabel: 'VAT',
      specialTaxHandling: 'none',
    },
    addressFormat: {
      ...defaultAddressFormat,
      postalCodeRegex: '^[A-Z]{1,2}[0-9][0-9A-Z]?\\s?[0-9][A-Z]{2}$',
      stateLabel: 'county',
      showState: false,
    },
    legal: { ...defaultLegalConfig, withdrawalPeriod: 14 },
  },

  // Germany
  DE: {
    channelSlug: 'de-eur',
    currency: 'EUR',
    defaultLanguage: 'de',
    tax: {
      pricesIncludeTax: true,
      showTaxSeparately: true,
      taxLabel: 'MwSt.',
      specialTaxHandling: 'none',
    },
    addressFormat: {
      ...defaultAddressFormat,
      postalCodeRegex: '^\\d{5}$',
      showState: false,
    },
    legal: { ...defaultLegalConfig, withdrawalPeriod: 14 },
  },

  // France
  FR: {
    channelSlug: 'fr-eur',
    currency: 'EUR',
    defaultLanguage: 'fr',
    tax: {
      pricesIncludeTax: true,
      showTaxSeparately: true,
      taxLabel: 'TVA',
      specialTaxHandling: 'none',
    },
    addressFormat: {
      ...defaultAddressFormat,
      postalCodeRegex: '^\\d{5}$',
      showState: false,
    },
    legal: { ...defaultLegalConfig, withdrawalPeriod: 14 },
  },

  // Switzerland
  CH: {
    channelSlug: 'ch-chf',
    currency: 'CHF',
    defaultLanguage: 'de',
    supportedLanguages: ['de', 'fr', 'it', 'en'],
    tax: {
      pricesIncludeTax: true,
      showTaxSeparately: true,
      taxLabel: 'MwSt.',
      specialTaxHandling: 'none',
    },
    addressFormat: {
      ...defaultAddressFormat,
      postalCodeRegex: '^\\d{4}$',
      showState: false,
    },
  },

  // Japan
  JP: {
    channelSlug: 'jp-jpy',
    currency: 'JPY',
    defaultLanguage: 'ja',
    tax: {
      pricesIncludeTax: true,
      showTaxSeparately: true,
      taxLabel: '消費税',
      specialTaxHandling: 'none',
    },
    addressFormat: {
      fieldOrder: [
        'postalCode',
        'countryArea',
        'city',
        'streetAddress1',
        'streetAddress2',
        'lastName',
        'firstName',
      ],
      requiredFields: [
        'postalCode',
        'countryArea',
        'city',
        'streetAddress1',
        'lastName',
        'firstName',
      ],
      postalCodeRegex: '^\\d{3}-?\\d{4}$',
      stateLabel: 'prefecture',
      showState: true,
    },
    delivery: { ...defaultDeliveryConfig, allowScheduling: true },
  },

  // Australia
  AU: {
    channelSlug: 'au-aud',
    currency: 'AUD',
    defaultLanguage: 'en',
    tax: {
      pricesIncludeTax: true,
      showTaxSeparately: true,
      taxLabel: 'GST',
      specialTaxHandling: 'none',
    },
    addressFormat: {
      ...defaultAddressFormat,
      postalCodeRegex: '^\\d{4}$',
      stateLabel: 'state',
      showState: true,
    },
  },

  // Canada
  CA: {
    channelSlug: 'ca-cad',
    currency: 'CAD',
    defaultLanguage: 'en',
    supportedLanguages: ['en', 'fr'],
    tax: {
      pricesIncludeTax: false,
      showTaxSeparately: true,
      taxLabel: 'Tax',
      specialTaxHandling: 'none',
    },
    addressFormat: {
      ...defaultAddressFormat,
      postalCodeRegex: '^[A-Z]\\d[A-Z]\\s?\\d[A-Z]\\d$',
      stateLabel: 'province',
      showState: true,
    },
  },

  // Italy
  IT: {
    channelSlug: 'it-eur',
    currency: 'EUR',
    defaultLanguage: 'it',
    tax: {
      pricesIncludeTax: true,
      showTaxSeparately: true,
      taxLabel: 'IVA',
      specialTaxHandling: 'none',
    },
    addressFormat: {
      ...defaultAddressFormat,
      postalCodeRegex: '^\\d{5}$',
      stateLabel: 'province',
      showState: true,
    },
    legal: { ...defaultLegalConfig, withdrawalPeriod: 14 },
  },

  // Spain
  ES: {
    channelSlug: 'es-eur',
    currency: 'EUR',
    defaultLanguage: 'es',
    tax: {
      pricesIncludeTax: true,
      showTaxSeparately: true,
      taxLabel: 'IVA',
      specialTaxHandling: 'none',
    },
    addressFormat: {
      ...defaultAddressFormat,
      postalCodeRegex: '^\\d{5}$',
      stateLabel: 'province',
      showState: true,
    },
    legal: { ...defaultLegalConfig, withdrawalPeriod: 14 },
  },
};

/**
 * Create market behavior by merging defaults with overrides
 */
export function createMarketBehavior(
  countryCode: string,
  override: Partial<MarketBehavior>
): MarketBehavior {
  const normalized = countryCode.toUpperCase();
  const marketDefault = MARKET_DEFAULTS[normalized] ?? {};

  return {
    ...defaultMarketBehavior,
    ...marketDefault,
    ...override,
    countryCode: normalized,
    // Deep merge nested objects
    tax: { ...defaultTaxConfig, ...marketDefault.tax, ...override.tax },
    addressFormat: {
      ...defaultAddressFormat,
      ...marketDefault.addressFormat,
      ...override.addressFormat,
    },
    delivery: { ...defaultDeliveryConfig, ...marketDefault.delivery, ...override.delivery },
    legal: { ...defaultLegalConfig, ...marketDefault.legal, ...override.legal },
  };
}

/**
 * Get channel slug for a country code
 */
export function getChannelSlug(countryCode: string): string {
  const normalized = countryCode.toUpperCase();
  const market = MARKET_DEFAULTS[normalized];
  return market?.channelSlug ?? defaultMarketBehavior.channelSlug;
}

/**
 * Get currency for a country code
 */
export function getCurrency(countryCode: string): string {
  const normalized = countryCode.toUpperCase();
  const market = MARKET_DEFAULTS[normalized];
  return market?.currency ?? defaultMarketBehavior.currency;
}
